import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AudioLines, Check, KeyRound, Loader2, MessageSquare, Plug, ShieldCheck } from 'lucide-react';
import { Card } from '@/components/ui';
import { useAuth, type ResponseStyle } from '@/lib/auth';
import { ApiError } from '@/lib/api';
import { changePassword, getTranscribeConfig, updateProfile, type TranscribeConfig } from '@/lib/api-client';
import {
  loadPreferredMode,
  savePreferredMode,
  type TranscriptionMode,
} from '@/contexts/TranscriptionContext';

const inputClass =
  'w-full rounded-md border border-stone-800 bg-stone-900 px-3 py-2 text-sm text-stone-200 placeholder:text-stone-600 focus:border-amber-700/60 focus:outline-none';
const labelClass = 'mb-1 block text-[10px] uppercase tracking-[0.2em] text-stone-500';

// Settings (P4 Section 4.2). Profile, the Plain English / Pro voice toggle
// (Section 1), password change, linked-account status, and help links.
export function Settings() {
  const { user, updateUser } = useAuth();

  return (
    <div className="min-h-screen bg-stone-950 px-5 py-6 sm:px-6">
      <div className="mx-auto w-full max-w-2xl space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl italic text-stone-100">Settings</h1>
          <Link
            to="/hearing"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Hearing
          </Link>
        </div>

        <ResponseStyleCard
          current={user?.responseStyle ?? 'PLAIN_ENGLISH'}
          onChange={(style) => updateUser({ responseStyle: style })}
        />
        <TranscriptionCard />
        <ProfileCard
          email={user?.email ?? ''}
          name={user?.name ?? ''}
          onSaved={(name) => updateUser({ name })}
        />
        <PasswordCard />
        <LinkedAccountsCard />
        <HelpCard />
      </div>
    </div>
  );
}

function ResponseStyleCard({
  current,
  onChange,
}: {
  current: ResponseStyle;
  onChange: (style: ResponseStyle) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function select(style: ResponseStyle): Promise<void> {
    if (style === current || busy) return;
    setError(null);
    setBusy(true);
    try {
      await updateProfile({ responseStyle: style });
      onChange(style);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change the voice.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card variant="default" padding="md" className="space-y-3">
      <div className="flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-amber-400" />
        <h2 className="text-sm uppercase tracking-[0.2em] text-stone-300">Response voice</h2>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-stone-500" /> : null}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <VoiceOption
          active={current === 'PLAIN_ENGLISH'}
          title="Plain English"
          blurb="A smart friend who knows the law. Every legal term translated, short sentences, tells you what to say next. Recommended."
          onClick={() => void select('PLAIN_ENGLISH')}
        />
        <VoiceOption
          active={current === 'PRO'}
          title="Pro"
          blurb="The senior-solicitor voice. Dense, formal, full citations and procedural language. For when you want the practitioner register."
          onClick={() => void select('PRO')}
        />
      </div>
      {error ? <p className="text-sm text-red-400">{error}</p> : null}
    </Card>
  );
}

function VoiceOption({
  active,
  title,
  blurb,
  onClick,
}: {
  active: boolean;
  title: string;
  blurb: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'rounded-md border border-amber-700/60 bg-amber-950/20 p-3 text-left'
          : 'rounded-md border border-stone-800 p-3 text-left hover:border-stone-700'
      }
    >
      <div className="flex items-center justify-between">
        <span className={active ? 'text-sm text-amber-300' : 'text-sm text-stone-200'}>{title}</span>
        {active ? <Check className="h-4 w-4 text-amber-400" /> : null}
      </div>
      <p className="mt-1 text-xs text-stone-500">{blurb}</p>
    </button>
  );
}

// Transcription quality (Sprint P5 Section 1.5). Deepgram Nova-3 is far more
// accurate on legal vocabulary but costs ~$0.0077/min; Web Speech is free but
// mangles courtroom terms. The choice is stored locally and applied to the next
// hearing. Deepgram can only be chosen when the server has a key configured.
function TranscriptionCard() {
  const [mode, setMode] = useState<TranscriptionMode>(loadPreferredMode);
  const [config, setConfig] = useState<TranscribeConfig | null>(null);

  useEffect(() => {
    let active = true;
    getTranscribeConfig()
      .then((cfg) => {
        if (active) setConfig(cfg);
      })
      .catch(() => {
        // leave config null -> treat Deepgram as unavailable
      });
    return () => {
      active = false;
    };
  }, []);

  const deepgramAvailable = config?.deepgramAvailable ?? false;
  const perMin = config?.pricePerMinUsd ?? 0.0077;

  function choose(next: TranscriptionMode): void {
    if (next === 'deepgram' && !deepgramAvailable) return;
    savePreferredMode(next);
    setMode(next);
  }

  return (
    <Card variant="default" padding="md" className="space-y-3">
      <div className="flex items-center gap-2">
        <AudioLines className="h-4 w-4 text-amber-400" />
        <h2 className="text-sm uppercase tracking-[0.2em] text-stone-300">Transcription quality</h2>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <VoiceOption
          active={mode === 'deepgram' && deepgramAvailable}
          title="Deepgram · high accuracy"
          blurb={
            deepgramAvailable
              ? `Nova-3 with a per-matter legal keyterm list. Far better on "Your Honour", "Briginshaw", party names. About $${perMin.toFixed(4)}/min, shown live during the hearing.`
              : 'Not configured on the server yet. Ask the admin to add the Deepgram key, then this becomes selectable.'
          }
          onClick={() => choose('deepgram')}
        />
        <VoiceOption
          active={mode === 'webspeech' || !deepgramAvailable}
          title="Web Speech · free"
          blurb="The in-browser engine (Chrome/Edge). Free and zero setup, but it mishears legal vocabulary. Used automatically if Deepgram drops out mid-hearing."
          onClick={() => choose('webspeech')}
        />
      </div>
      {!deepgramAvailable ? (
        <p className="text-xs text-stone-500">
          Deepgram is currently unavailable, so hearings use Web Speech.
        </p>
      ) : null}
    </Card>
  );
}

function ProfileCard({
  email,
  name,
  onSaved,
}: {
  email: string;
  name: string;
  onSaved: (name: string) => void;
}) {
  const [value, setValue] = useState(name);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function save(): Promise<void> {
    setError(null);
    setNotice(null);
    if (!value.trim()) {
      setError('Name cannot be empty.');
      return;
    }
    setBusy(true);
    try {
      await updateProfile({ name: value.trim() });
      onSaved(value.trim());
      setNotice('Saved.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card variant="default" padding="md" className="space-y-3">
      <h2 className="text-sm uppercase tracking-[0.2em] text-stone-300">Profile</h2>
      <div>
        <label className={labelClass} htmlFor="pf-email">
          Email
        </label>
        <input id="pf-email" type="email" value={email} disabled className={`${inputClass} opacity-60`} />
      </div>
      <div>
        <label className={labelClass} htmlFor="pf-name">
          Name
        </label>
        <input
          id="pf-name"
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Your name"
          className={inputClass}
        />
      </div>
      {error ? <p className="text-sm text-red-400">{error}</p> : null}
      {notice ? <p className="text-sm text-emerald-400">{notice}</p> : null}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void save()}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-md border border-stone-700 px-3 py-1.5 text-sm text-stone-200 hover:border-amber-700/60 disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Save profile
        </button>
      </div>
    </Card>
  );
}

function PasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function save(): Promise<void> {
    setError(null);
    setNotice(null);
    if (next.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    setBusy(true);
    try {
      await changePassword(current, next);
      setNotice('Password changed.');
      setCurrent('');
      setNext('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card variant="default" padding="md" className="space-y-3">
      <div className="flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-stone-400" />
        <h2 className="text-sm uppercase tracking-[0.2em] text-stone-300">Password</h2>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className={labelClass} htmlFor="pw-cur">
            Current
          </label>
          <input
            id="pw-cur"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="pw-new">
            New
          </label>
          <input
            id="pw-new"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className={inputClass}
          />
        </div>
      </div>
      {error ? <p className="text-sm text-red-400">{error}</p> : null}
      {notice ? <p className="text-sm text-emerald-400">{notice}</p> : null}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void save()}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-md border border-stone-700 px-3 py-1.5 text-sm text-stone-200 hover:border-amber-700/60 disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Change password
        </button>
      </div>
    </Card>
  );
}

function LinkedAccountsCard() {
  return (
    <Card variant="default" padding="md" className="space-y-3">
      <div className="flex items-center gap-2">
        <Plug className="h-4 w-4 text-stone-400" />
        <h2 className="text-sm uppercase tracking-[0.2em] text-stone-300">Linked accounts</h2>
      </div>
      <p className="text-xs text-stone-500">
        Dropbox and Microsoft 365 are connected server side via long lived OAuth refresh tokens, so
        deep mode can search your files and email. These are shared by the deployment and managed by
        the admin; there is no per user reconnect here yet.
      </p>
      <div className="space-y-2">
        <AccountRow name="Dropbox" detail="Files, contracts, correspondence (deep mode)" />
        <AccountRow name="Microsoft 365" detail="Outlook email, SharePoint, OneDrive (deep mode)" />
      </div>
    </Card>
  );
}

function AccountRow({ name, detail }: { name: string; detail: string }) {
  return (
    <div className="flex items-center justify-between rounded-md border border-stone-800/60 bg-stone-900/40 px-3 py-2">
      <div>
        <p className="text-sm text-stone-200">{name}</p>
        <p className="text-[11px] text-stone-500">{detail}</p>
      </div>
      <span className="inline-flex items-center gap-1 text-[11px] text-stone-500">
        <ShieldCheck className="h-3.5 w-3.5" /> Managed by admin
      </span>
    </div>
  );
}

function HelpCard() {
  return (
    <Card variant="default" padding="md" className="space-y-2">
      <h2 className="text-sm uppercase tracking-[0.2em] text-stone-300">Help</h2>
      <p className="text-xs text-stone-500">
        Press <kbd className="rounded bg-stone-800 px-1">?</kbd> anywhere for the keyboard shortcuts.
        The <span className="text-stone-300">?</span> icon in the header opens the help drawer with
        fast vs deep mode, the live classifier, hearing prep, and the courtroom protocol checklist.
      </p>
      <button
        type="button"
        onClick={() => {
          try {
            localStorage.removeItem('cc.onboarding.done');
            localStorage.setItem('cc.onboarding.pending', '1');
          } catch {
            // ignore
          }
          window.location.href = '/hearing';
        }}
        className="text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
      >
        Replay the welcome tour
      </button>
    </Card>
  );
}
